/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import DS from 'ember-data';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import FacebookLeadAdsService, { FacebookBusinessPageData } from 'cio/services/facebook-lead-ads';
import CustomFormsService from 'cio/services/custom-forms';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CampaignModel, { FormTriggeredCampaignAttributeFilters } from 'cio/models/campaign';
import GroupCondition from 'cio/app-models/conditions/group';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import { tracked } from '@glimmer/tracking';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import CustomForm from 'cio/models/custom-form';
import { task } from 'ember-concurrency';
import { FormIntegrationDetailPanes } from 'cio/components/forms/custom/integration-details';

export interface FormArgs {
  campaign: CampaignModel;
  filters: Record<string, unknown>[];
  restartMinInterval: number;
  attributeFilters: FormTriggeredCampaignAttributeFilters;
  updateAttributeFilters: (attributeFilters: FormTriggeredCampaignAttributeFilters) => unknown;
}

enum Views {
  selectForm = 'selectForm',
  connectForm = 'connectForm',
  customForm = 'customForm',
  typeForm = 'typeForm',
  jotForm = 'jotForm',
  fbLeadAds = 'fbLeadAds',
}

export default class Form extends Component<FormArgs> {
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;
  @tracked showRequestFormProviderModal = false;

  // relevant for enabling the integration (connecting to Facebook Business Page):
  @tracked userAccessToken: string | null = null;
  @tracked facebookAccountName: string | null = null;
  @tracked pages: FacebookBusinessPageData[] | null = null;

  readonly views = Views;
  @tracked view = Views.selectForm;
  readonly panes = FormIntegrationDetailPanes;
  @tracked editing = {};
  @tracked customFormToConnect: CustomForm | null = null;
  formConnected = false;

  typeformImage = new URL('cio/assets/images/integrations/typeform.svg', import.meta.url).toString();
  jotformImage = new URL('cio/assets/images/integrations/jotform.svg', import.meta.url).toString();

  constructor(owner: unknown, args: FormArgs) {
    super(owner, args);

    this.#initAllTheValues();
  }

  get selectedForms() {
    return this.args.attributeFilters.children.map((condition: FormTriggeredCampaignAttributeFilters) => {
      let [formCondition, dataFiltersCondition] = condition.children;
      // type hack to get the correct type
      let formId = (formCondition as unknown as FormEventAttributeCondition).value;
      let form: CustomForm | FacebookLeadAdsForm | undefined =
        this.facebookLeadAds.forms.find((f) => f.facebookFormId === formId) ??
        this.customForms.forms.find((f) => f.formIdLowercase === formId);
      return {
        form,
        eventDataFilters: dataFiltersCondition ?? null,
      };
    });
  }

  get selectableForms(): (CustomForm | FacebookLeadAdsForm)[] {
    let selectedFormsMap = new Set(this.selectedForms.map(({ form }) => form?.formIdLowercase));
    return [...this.facebookLeadAds.connectedForms, ...this.customForms.connectedOrDraftForms].filter((form) => {
      return !selectedFormsMap.has(form.formIdLowercase);
    });
  }

  get formsInitialized() {
    return this.facebookLeadAds.initialized && this.customForms.initialized;
  }

  async #initAllTheValues() {
    await Promise.all([this.facebookLeadAds.initialize(), this.customForms.fetchForms()]);

    if (this.selectedForms.length === 0) {
      this.view = Views.connectForm;
      return;
    }
  }

  get showBackButton() {
    if (this.selectedForms.length > 0 && this.view === Views.connectForm) {
      return true;
    }
    if (this.view !== Views.connectForm && this.view !== Views.selectForm) {
      return true;
    }
    return false;
  }

  @action
  onBack() {
    if (this.selectedForms.length > 0 && this.view === Views.connectForm) {
      this.changeView(Views.selectForm);
      return;
    }
    if (this.view === Views.customForm) {
      this.onCloseConnectForm();
      return;
    }
    if (this.view !== Views.connectForm && this.view !== Views.selectForm) {
      this.changeView(Views.connectForm);
      return;
    }
  }

  @action addForm(form?: CustomForm | FacebookLeadAdsForm) {
    let clone = this.args.attributeFilters.duplicate();
    clone.children = [
      ...clone.children,
      GroupCondition.create({
        operator: 'and',
        children: [
          FormEventAttributeCondition.create({
            formType: form?.formType,
            shouldValidate: true,
          }),
        ],
      }),
    ];
    this.args.updateAttributeFilters(clone as FormTriggeredCampaignAttributeFilters);
  }

  @action selectForm(index: number, f: CustomForm | FacebookLeadAdsForm) {
    let selectedForms = [...this.selectedForms];
    this.editing = {};
    let form = selectedForms[index]?.form;
    if (form?.formIdLowercase === f.formId) {
      // selecting the same form as previously selected: no need to do anything
      return;
    }

    let { formIdLowercase: value, formType } = f;

    // clone whole "attributeFilters"
    let clone = this.args.attributeFilters.duplicate() as FormTriggeredCampaignAttributeFilters;
    // then clone specific "GroupCondition" which contains FormEventAttributeCondition as
    let clonedForm = clone.children[index].duplicate();
    clonedForm.children = [
      FormEventAttributeCondition.create({
        formType,
        value,
        shouldValidate: true,
      }),
      // add new FormEventAttributeCondition and keep event filters if any
      ...clonedForm.children.slice(1),
    ];
    clone.children.splice(index, 1, clonedForm); // replace previous state with new selection
    this.args.updateAttributeFilters(clone as FormTriggeredCampaignAttributeFilters);
  }

  @action removeForm(index: number) {
    let clone = this.args.attributeFilters.duplicate();
    clone.children.splice(index, 1); // .duplicate does create new "children" array ref
    this.args.updateAttributeFilters(clone as FormTriggeredCampaignAttributeFilters);
    if (clone.children.length === 0) {
      this.changeView(Views.connectForm);
    }
  }

  @action addFilter(index: number) {
    // in order to not mutate data, we need to clone and assign the new filter
    // clone whole "attributeFilters"
    let clone = this.args.attributeFilters.duplicate() as FormTriggeredCampaignAttributeFilters;
    // then clone specific "GroupCondition" which contains FormEventAttributeCondition as
    let clonedForm = clone.children[index].duplicate();
    let [formCondition, eventDataFilters] = clonedForm.children as [FormEventAttributeCondition, GroupCondition | null];
    // if not event data filters are present, then create new GroupCondition
    if (eventDataFilters == null) {
      eventDataFilters = GroupCondition.create({
        operator: 'and',
        children: [],
      });
    } else {
      eventDataFilters = eventDataFilters.duplicate();
    }
    // then add new Filter to Event data GroupCondition
    eventDataFilters.children.push(
      EventAttributeCondition.create({
        event: 'form_submit',
        name: '',
        operator: 'eq',
        value: '',
        shouldValidate: false,
      }),
    );
    // combine back together
    clonedForm.children = [formCondition, eventDataFilters];
    clone.children.splice(index, 1, clonedForm); // replace previous state with new selection
    this.args.updateAttributeFilters(clone as FormTriggeredCampaignAttributeFilters);
  }

  @action removeFilter(index: number, _filter: EventAttributeCondition, filterIndex: number) {
    // in order to not mutate data, we need to clone and assign the new filter
    // clone whole "attributeFilters"
    let clone = this.args.attributeFilters.duplicate() as FormTriggeredCampaignAttributeFilters;
    // then clone specific "GroupCondition" which contains FormEventAttributeCondition as
    let clonedForm = clone.children[index].duplicate();
    let [formCondition, eventDataFilters] = clonedForm.children as [FormEventAttributeCondition, GroupCondition];
    eventDataFilters = eventDataFilters.duplicate();
    // remove event data filter
    eventDataFilters.children.splice(filterIndex, 1);

    // combine back together
    // if last filter removed: remove whole event data filters condition
    if (eventDataFilters.children.length === 0) {
      clonedForm.children = [formCondition];
    } else {
      clonedForm.children = [formCondition, eventDataFilters];
    }

    clone.children.splice(index, 1, clonedForm); // replace previous state with new selection
    this.args.updateAttributeFilters(clone as FormTriggeredCampaignAttributeFilters);
  }

  private setForm(form: FacebookLeadAdsForm | CustomForm) {
    // create a new form row if either:
    // - there are no form rows, because first form is being connected
    // - last form row already has a selected form
    if (this.selectedForms.length === 0 || this.selectedForms[this.selectedForms.length - 1]?.form != null) {
      this.addForm(form);
    }
    this.selectForm(this.selectedForms.length - 1, form);
  }

  refetchForms = task(async () => {
    await this.customForms.fetchForms();
  });

  @action
  async onConnectCustomForm(form: CustomForm) {
    await this.refetchForms.perform();
    this.onConnectForm(form);
  }

  @action
  connectCustomForm() {
    this.customFormToConnect = this.store.createRecord('custom-form');
    this.changeView(Views.customForm);
  }

  @action
  cancelConnectForm() {
    this.customFormToConnect = null;
    this.formConnected = false;
  }

  @action
  onConnectForm(form: FacebookLeadAdsForm | CustomForm) {
    this.setForm(form);
    this.formConnected = true;
  }

  @action
  onCloseConnectForm() {
    this.changeView(this.formConnected ? Views.selectForm : Views.connectForm);
  }

  @action
  changeView(view: Views) {
    this.view = view;
    if (view !== Views.customForm) {
      this.cancelConnectForm();
    }
  }

  @action
  toggleEdit(index?: number, value = false) {
    if (index != null) {
      this.editing = {
        ...this.editing,
        [index]: value,
      };
    } else {
      this.editing = {};
    }
  }
  /* eslint-disable spellcheck/spell-checker */
  readonly providersList = [
    {
      link: 'https://customer.io/docs/connected-forms/#unbounce',
      iconUrl: new URL('cio/assets/images/integrations/unbounce.svg', import.meta.url).toString(),
      title: 'Unbounce',
      styleAsSetupGuideOption: true,
      dataTestSetupFormsButton: 'unbounce',
    },
    {
      link: 'https://customer.io/docs/connected-forms/#formstack',
      iconUrl: new URL('cio/assets/images/integrations/formstack.svg', import.meta.url).toString(),
      title: 'Formstack',
      styleAsSetupGuideOption: true,
      dataTestSetupFormsButton: 'formstack',
    },
    {
      link: 'https://customer.io/docs/connected-forms/#instapage',
      iconUrl: new URL('cio/assets/images/integrations/instapage.svg', import.meta.url).toString(),
      title: 'Instapage',
      styleAsSetupGuideOption: true,
      dataTestSetupFormsButton: 'instapage',
    },
    {
      link: 'https://customer.io/docs/connected-forms/#squarespace',
      iconUrl: new URL('cio/assets/images/integrations/squarespace.svg', import.meta.url).toString(),
      title: 'Squarespace',
      styleAsSetupGuideOption: true,
      dataTestSetupFormsButton: 'squarespace',
    },
    {
      link: 'https://customer.io/docs/connected-forms/#webflow',
      iconUrl: new URL('cio/assets/images/integrations/webflow.svg', import.meta.url).toString(),
      title: 'Webflow',
      styleAsSetupGuideOption: true,
      dataTestSetupFormsButton: 'webflow',
    },
    {
      link: 'https://customer.io/docs/connected-forms/#netlify',
      iconUrl: new URL('cio/assets/images/integrations/netlify.svg', import.meta.url).toString(),
      title: 'Netlify',
      styleAsSetupGuideOption: true,
      dataTestSetupFormsButton: 'netlify',
    },
    {
      link: 'https://customer.io/docs/connected-forms/#wpforms',
      iconUrl: new URL('cio/assets/images/integrations/wpforms.svg', import.meta.url).toString(),
      title: 'WPForms',
      styleAsSetupGuideOption: true,
      dataTestSetupFormsButton: 'wpforms',
    },
  ];
}
